import axios from "axios";
import { toast } from "sonner";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach bearer from localStorage as fallback (mobile browsers may block third-party cookies)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Super-admin impersonation: forward the target tenant to the backend
  const imp = localStorage.getItem("impersonate_tenant");
  if (imp) {
    config.headers["X-Impersonate-Tenant"] = imp;
  }
  return config;
});

// Global response interceptor — handles:
//   1) demo workspace's read-only 403 (toast + normalise)
//   2) 401 Access-token expired → silently POST /auth/refresh (uses the
//      httpOnly 7-day refresh cookie) and retry the original request once.
//      Fixes the 12h "kicked to login mid-day" pain point.
let lastDemoToastAt = 0;
let refreshInFlight = null;   // single-flight promise — prevents 20 parallel refreshes on tab wake-up
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const d = err?.response?.data?.detail;
    if (d && typeof d === "object" && d.code === "demo_read_only") {
      const now = Date.now();
      if (now - lastDemoToastAt > 1500) {
        lastDemoToastAt = now;
        toast.info(d.message || "This is a live demo — sign up to make changes.");
      }
      if (err.response) err.response.data.detail = d.message;
      return Promise.reject(err);
    }

    // Silent JWT refresh. Retry the failed request exactly once. Skip the
    // /auth/* endpoints themselves (login/refresh/logout) so we never loop.
    const status = err?.response?.status;
    const cfg = err?.config;
    const url = String(cfg?.url || "");
    const isAuthEndpoint = url.includes("/auth/login")
      || url.includes("/auth/refresh")
      || url.includes("/auth/logout");
    if (status === 401 && cfg && !cfg._retried && !isAuthEndpoint) {
      cfg._retried = true;
      try {
        // Coalesce parallel 401s into ONE refresh call
        if (!refreshInFlight) {
          refreshInFlight = api.post("/auth/refresh").finally(() => {
            // Clear the singleton after resolve or fail so future 401s can retry
            setTimeout(() => { refreshInFlight = null; }, 0);
          });
        }
        const { data } = await refreshInFlight;
        if (data?.access_token) {
          try { localStorage.setItem("access_token", data.access_token); } catch {}
          cfg.headers = cfg.headers || {};
          cfg.headers.Authorization = `Bearer ${data.access_token}`;
        }
        return api(cfg);
      } catch (refreshErr) {
        // Refresh failed → refresh cookie is gone/expired. Clear stored
        // token so the next navigation cleanly bounces to /login (the
        // AuthContext already handles a 401 on /auth/me).
        try { localStorage.removeItem("access_token"); } catch {}
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(path) {
  if (!path) return "";
  const token = localStorage.getItem("access_token");
  return `${API}/files/${path}?auth=${encodeURIComponent(token || "")}`;
}

// Resolve a photo/file OBJECT to a browser src. ClickUp-synced media carries a
// ready `url` (proxy or CDN) and no local `path`; local uploads carry `path`.
// Using fileUrl(p.path) blindly yields /api/files/undefined for ClickUp media.
export function mediaSrc(p) {
  if (!p) return "";
  if (typeof p === "string") return fileUrl(p);
  const u = p.url || "";
  if (u) {
    if (u.startsWith("http")) return u;
    const base = API.replace(/\/api$/, "");
    const abs = `${base}${u.startsWith("/") ? "" : "/"}${u}`;
    if (u.includes("/api/files/") && !u.includes("auth=")) {
      const token = localStorage.getItem("access_token") || "";
      return `${abs}${abs.includes("?") ? "&" : "?"}auth=${encodeURIComponent(token)}`;
    }
    return abs;
  }
  return p.path ? fileUrl(p.path) : "";
}
